import { AsyncStatus } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import { useMemo } from 'react';

import { AwayStatus } from 'apis/presence';
import { searchApi } from 'apis/search';
import { teamsApi } from 'apis/teams';
import { sortPresence } from 'components/menu/utils';
import { PresenceListSection } from 'components/presence/components/presenceListSection';
import { PresenceSectionSkeleton } from 'components/presence/components/presenceSectionSkeleton';
import { PresenceSectionWarning } from 'components/presence/components/presenceSectionWarning';
import type {
  PresenceStateT,
  UserPresence,
} from 'components/presence/reducers/presence';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

type TeamPresenceListSectionsProps = {
  presence: PresenceStateT;
  presenceAsyncStatus: AsyncStatus;
  profile: User;
  awayStatuses: AwayStatus[];
};

export const TeamPresenceListSections = ({
  presence,
  presenceAsyncStatus,
  profile,
  awayStatuses,
}: TeamPresenceListSectionsProps): JSX.Element => {
  const { presenceSidebarShowSelf } = useFeatureFlag([
    'presence-sidebar-show-self',
  ]);

  const { allPresence } = presence;
  const ownUserId = profile.id;

  const { teams, teamsLoading, teamsFailed } =
    searchApi.useReadAdvancedSearchResultsQuery(
      {
        params: {
          scope: 'users',
          query: '',
          filters: {
            strategy: 'all',
            conditions: [
              {
                field: 'id',
                operator: 'is_one',
                values: [{ label: '', value: profile.id }],
              },
            ],
          },
          strict: true,
          page: 1,
        },
      },
      {
        selectFromResult: ({ data, ...rest }) => {
          const userResult =
            data?.data.results.filter(
              (result) => result.hits_type === 'user',
            ) ?? [];
          const hits = userResult.length > 0 ? userResult[0].hits : [];
          const teamIds = hits.length > 0 ? hits[0].teams : [];

          return {
            teams: teamIds,
            teamsLoading: rest.isLoading,
            teamsFailed: rest.isError,
          };
        },
      },
    );

  const { teamMembers, teamMembersLoading, teamMembersFailed } =
    searchApi.useReadAdvancedSearchResultsQuery(
      {
        params: {
          scope: 'users',
          query: '',
          filters: {
            strategy: 'all',
            conditions: [
              {
                field: 'teams',
                operator: 'includes_one',
                values:
                  teams?.map((teamId) => ({ label: '', value: teamId })) ?? [],
              },
            ],
          },
          strict: true,
          page: 1,
        },
      },
      {
        skip: !teams || teams.length === 0,
        selectFromResult: ({ data, ...rest }) => {
          const userResult =
            data?.data.results.filter(
              (result) => result.hits_type === 'user',
            ) ?? [];
          return {
            teamMembers: userResult.length > 0 ? userResult[0].hits : [],
            teamMembersLoading: rest.isLoading,
            teamMembersFailed: rest.isError,
          };
        },
      },
    );

  const { teamNames } = teamsApi.useReadTeamsQuery(
    {},
    {
      skip: !teams || teams.length === 0,
      selectFromResult: ({ data }) => {
        return {
          teamNames:
            data?.data.teams
              .filter((team) => (teams ?? []).includes(team.id))
              .reduce((acc, team) => ({ [team.id]: team.name, ...acc }), {}) ??
            {},
        };
      },
    },
  );

  const presenceMap: { [userId: string]: UserPresence } = useMemo(
    () =>
      allPresence.reduce(
        (acc, presence) => ({ ...acc, [presence.user.id]: presence }),
        {},
      ),
    [allPresence],
  );

  const teamPresence: { id: string; members: UserPresence[] }[] = useMemo(
    () =>
      (teams ?? [])
        .map((team) => {
          const teamMemberPresence = teamMembers
            .filter(
              (teamMember) =>
                (teamMember.teams ?? ([] as string[])).includes(team) &&
                (presenceSidebarShowSelf || teamMember.id !== ownUserId), // Conditionally exclude own user from team sections
            )
            .map((searchHit) => presenceMap[searchHit.id])
            .filter(
              (presence: UserPresence | undefined) => presence !== undefined,
            );
          return {
            id: team,
            members: sortPresence(teamMemberPresence),
          };
        })
        .filter((team) => team.members.length > 0),
    [presenceMap, ownUserId, teamMembers, teams, presenceSidebarShowSelf],
  );

  const presenceLoading = presenceAsyncStatus === 'loading';

  // We don't look at presenceFailed here, as we don't show this component if presence has failed
  const isError = teamsFailed || teamMembersFailed;
  if (isError) {
    return <PresenceSectionWarning title="Loading team members failed.." />;
  }

  if (presenceLoading || teamsLoading || teamMembersLoading) {
    return (
      <>
        <PresenceSectionSkeleton />
        <PresenceSectionSkeleton />
      </>
    );
  }

  return (
    <>
      {teamPresence.map((team, teamIndex) => {
        const teamName =
          (teamNames && teamNames[team.id]) || `Team ${teamIndex + 1}`;
        return (
          <PresenceListSection
            key={team.id}
            title={`${teamName} (${team.members.length})`}
            presenceList={team.members}
            profile={profile}
            ownPresence={presence.presence}
            awayStatuses={awayStatuses}
          />
        );
      })}
    </>
  );
};
